import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useCollectionBinding } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { CTA_OUTLINE, CtaStyles, ctaOverPhoto } from './_cta';

interface SpecialOffer {
  title?: string;
  description?: string;
  disclaimer?: string;
  image?: string;
  alt_text?: string;
}

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Special offer background' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_website_url',
      suggestedFields: ['property_website_url', 'property_apply_now_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'LEARN MORE' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function SpecialOffers() {
  const title = useContentToken('specialoffers.title');

  const description = useContentToken('specialoffers.description');

  const bgImage = useSlotGroup('specialoffers.bg_image', bgImageDef);
  const ctaLink = useSlotGroup('specialoffers.cta_link', ctaLinkDef);

  const offers = useCollectionBinding<SpecialOffer>('specialoffers.items', {
    collection: 'special_offers',
  });

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <EditableSlotGroup groupPath="specialoffers.bg_image" definition={bgImageDef}>
        <div className="absolute inset-0">
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
          <div className="absolute inset-0 special-offers-overlay" />
        </div>
      </EditableSlotGroup>

      <div ref={contentRef} className="reveal-fade-up relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white" style={ctaOverPhoto}>
        <CtaStyles />
        <EditableToken path="specialoffers.title" defaultValue="Thinking about calling us your new home?" as="h2" className="font-heading text-4xl sm:text-5xl md:text-6xl font-normal mb-6 leading-tight">
          {title}
        </EditableToken>

        <EditableToken path="specialoffers.description" defaultValue="Call us today for our latest leasing specials or visit our Special Offer page and join our future residents club!" as="p" className="font-body text-lg sm:text-xl mb-10 leading-relaxed opacity-90 max-w-2xl mx-auto">
          {description}
        </EditableToken>

        <EditableSlotGroup groupPath="specialoffers.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '#'}
            target={ctaLink._target || '_self'}
            className={CTA_OUTLINE}
          >
            {ctaLink.label || 'LEARN MORE'}
          </a>
        </EditableSlotGroup>

        {offers && offers.length > 0 && (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {offers.map((offer, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm p-6 text-left"
              >
                {offer.title && (
                  <h3 className="font-heading text-xl font-normal mb-2">{offer.title}</h3>
                )}
                {offer.description && (
                  <p className="font-body text-sm opacity-80">{offer.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .special-offers-overlay {
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--vibe-color-primary) 85%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 65%, black) 100%
          );
          opacity: 0.75;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SpecialOffers',
  displayName: 'Special Offers',
  tokens: {
    'specialoffers.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Thinking about calling us your new home?',
    },
    'specialoffers.description': {
      label: 'Description',
      type: 'text',
      defaultValue: 'Call us today for our latest leasing specials or visit our Special Offer page and join our future residents club!',
    },
  },
  slotGroups: {
    'specialoffers.bg_image': bgImageDef,
    'specialoffers.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'specialoffers.items': {
      label: 'Special Offers',
      collection: 'special_offers',
    },
  },
};

(SpecialOffers as any).contentSchema = contentSchema;

export default SpecialOffers;
